// Models the in-app agent can run on, grouped by provider.
//
// The server decides the provider from the model id (supabase/functions/_shared/models.ts);
// src/test/model-registry.test.ts keeps this list and that registry in sync.

export const CLAUDE_OPUS_MODEL_ID = "claude-opus-5-5";
export const CLAUDE_SONNET_MODEL_ID = "claude-sonnet-5-5";
export const CLAUDE_HAIKU_MODEL_ID = "claude-haiku-5-5";

export const DEFAULT_AGENT_MODEL_ID = CLAUDE_SONNET_MODEL_ID;

export type ModelProviderGroup = "anthropic" | "gateway";

export interface AgentModelOption {
  id: string;
  label: string;
  description: string;
  provider: ModelProviderGroup;
}

export interface AgentModelGroup {
  provider: ModelProviderGroup;
  label: string;
  models: AgentModelOption[];
}

export const AGENT_MODEL_GROUPS: AgentModelGroup[] = [
  {
    provider: "anthropic",
    label: "Anthropic",
    models: [
      { id: CLAUDE_OPUS_MODEL_ID, label: "Claude Opus 5.5", description: "Most capable Claude model, for complex multi-step work", provider: "anthropic" },
      { id: CLAUDE_SONNET_MODEL_ID, label: "Claude Sonnet 5.5", description: "Best balance of speed and capability (default)", provider: "anthropic" },
      { id: CLAUDE_HAIKU_MODEL_ID, label: "Claude Haiku 5.5", description: "Fastest and most affordable Claude model", provider: "anthropic" },
    ],
  },
  {
    provider: "gateway",
    label: "Gemini / OpenAI",
    models: [
      { id: "google/gemini-3.1-pro-preview", label: "Gemini 3.1 Pro", description: "Google Gemini 3.1 Pro (preview)", provider: "gateway" },
      { id: "google/gemini-3-pro-preview", label: "Gemini 3 Pro", description: "Google Gemini 3 Pro (preview)", provider: "gateway" },
      { id: "google/gemini-3-flash-preview", label: "Gemini 3 Flash", description: "Fast Google model (preview)", provider: "gateway" },
      { id: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro", description: "Google Gemini 2.5 Pro", provider: "gateway" },
      { id: "google/gemini-2.5-flash", label: "Gemini 2.5 Flash", description: "Google Gemini 2.5 Flash", provider: "gateway" },
      { id: "google/gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite", description: "Lightest Google model", provider: "gateway" },
      { id: "openai/gpt-5.2", label: "GPT-5.2", description: "OpenAI GPT-5.2", provider: "gateway" },
      { id: "openai/gpt-5", label: "GPT-5", description: "OpenAI GPT-5", provider: "gateway" },
      { id: "openai/gpt-5-mini", label: "GPT-5 mini", description: "Smaller, faster GPT-5", provider: "gateway" },
      { id: "openai/gpt-5-nano", label: "GPT-5 nano", description: "Smallest GPT-5", provider: "gateway" },
    ],
  },
];

export const AGENT_MODELS: AgentModelOption[] = AGENT_MODEL_GROUPS.flatMap((g) => g.models);

const KNOWN_IDS = new Set(AGENT_MODELS.map((m) => m.id));

/** Aliases and retired ids that may still be stored in settings or custom agents. */
const LEGACY_MODEL_IDS: Record<string, string> = {
  opus: CLAUDE_OPUS_MODEL_ID,
  sonnet: CLAUDE_SONNET_MODEL_ID,
  haiku: CLAUDE_HAIKU_MODEL_ID,
  "claude-opus-4-20250514": CLAUDE_OPUS_MODEL_ID,
  "claude-sonnet-4-20250514": CLAUDE_SONNET_MODEL_ID,
  "claude-haiku-4-20250514": CLAUDE_HAIKU_MODEL_ID,
};

/**
 * Map a stored or legacy model id to a current one. Known ids pass through,
 * legacy Claude ids map to the same family, anything else falls back to
 * `fallback` (the agent default unless given).
 */
export function normalizeModelId(id: string | null | undefined, fallback: string = DEFAULT_AGENT_MODEL_ID): string {
  if (!id) return fallback;
  if (KNOWN_IDS.has(id)) return id;
  const legacy = LEGACY_MODEL_IDS[id.toLowerCase()];
  if (legacy) return legacy;
  const family = /^claude-.*\b(opus|sonnet|haiku)\b/i.exec(id)?.[1]?.toLowerCase();
  return family ? LEGACY_MODEL_IDS[family] : fallback;
}

export function getAgentModel(id: string | null | undefined): AgentModelOption | undefined {
  return AGENT_MODELS.find((m) => m.id === id);
}
